<script lang="ts">
    import { _, isLoading, locale } from 'svelte-i18n';

    import { afterUpdate, onMount } from 'svelte';
    import { Router, Route } from 'svelte-routing';
    import Spinner from './lib/shared/Spinner/Spinner.svelte';
    import { debug, lastClickedPosition, mainMenu, onMapLoaded } from './stores/store';
    import srcLogoAutoMapaLastMile from './lib/shared/Icons/svg/official/AM_etoll.svg';
    import TargeoMap from './lib/shared/TargeoMap.svelte';
    import MainMenuVertical from './lib/shared/Menu/MainMenuVertical.svelte';
    import './locale/loadLocalesAsync';
    import {
        api,
        clientSettings,
        connections_filter_config,
        customerSettings,
        jobs_filter_config,
    } from './stores/api';
    import { isMapLoaded } from './stores/store';
    import Dropdown2 from './lib/components/MenuDropdown/Dropdown2.svelte';
    import SimpleStatus from './lib/shared/SimpleStatus.svelte';
    import { accountName } from './stores/store';
    import { Modals, closeModal, openModal } from 'svelte-modals';
    import { SettingsApi } from './lib/components/Settings/SettingsApi';
    import AppMainStatus from './lib/components/AppMainStatus/AppMainStatus.svelte';
    import { get } from 'svelte/store';
    import {
        ClientSettingsSchema,
        defaultClientSettings,
    } from './lib/components/Settings/settings';
    import ModalInfo from './lib/shared/Modals/ModalInfo.svelte';
    import MenuContextParent from './lib/components/MenuDropdown/MenuContext/MenuContextParent.svelte';
    import { MonitorSessionStatus } from './lib/components/Status/MonitorSessionStatus';
    import { captureMessage } from '@sentry/svelte';
    import { app, toPermissions } from './stores/app';
    import {
        accountBlocked,
        registerAccountBlockedInterceptor,
    } from './lib/shared/Permissions/accountBlocked';
    import AccountBlockedScreen from './lib/shared/Permissions/AccountBlockedScreen.svelte';
    import { NotificationSystem } from './lib/components/NotificationSystem/NotificationSystem';
    import { Umami } from './lib/utilities/Umami';

    export let url = '';
    export let initialSettings = {};

    let loadingSettings: boolean = true;
    let error: string = '';
    let appIsReady: boolean = false;
    let appLoading: boolean = true;
    let leftPageWidth: number = 600;
    let draggableBorderWidth: number = 10;
    let draggableBorder: HTMLElement;

    function resizeMap() {
        if (
            typeof Targeo !== 'undefined' &&
            typeof Targeo.Base !== 'undefined' &&
            typeof Targeo.Base.Map !== 'undefined'
        ) {
            Targeo.Base.Map.updateSize();
        }
    }

    afterUpdate(() => {
        resizeMap();
    });

    const onLogout = () => {
        console.info('function_onLogout');
        Umami.event('logout_auto');
        openModal(ModalInfo, {
            text: $_('logout_info_and_what_next'),
            onClick: () => {
                if (typeof initialSettings.onLogout === 'function') {
                    initialSettings.onLogout();
                }
            },
        });
    };

    $api.setToken(initialSettings?.token || '');
    $api.onLogout(onLogout);
    new MonitorSessionStatus(onLogout);

    async function isLoadingPromise(): Promise<void> {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (!$isLoading) {
                    clearInterval(interval);
                    resolve();
                }
            }, 250);
        });
    }

    async function isjQueryLoaded(): Promise<void> {
        return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
                if (window.jQuery) {
                    clearInterval(interval);
                    resolve();
                }
            }, 250);
        });
    }

    document.addEventListener('click', event => {
        $lastClickedPosition.x = event.clientX;
        $lastClickedPosition.y = event.clientY;
    });

    onMount(() => {
        // przed pierwszym żądaniem - konto zablokowane dostaje 403 już na etapie autentykacji
        registerAccountBlockedInterceptor();
        $api.setApiUrl(initialSettings?.apiUrl);
        Promise.all([
            isLoadingPromise(),
            isjQueryLoaded(),
            SettingsApi.getCustomerSettings(get(api)),
            SettingsApi.getClientSettings(get(api), 'settings', ClientSettingsSchema),
            SettingsApi.isAdmin($api),
            SettingsApi.getSettingsReadonly($api),
            SettingsApi.getMyPermissions($api),
            // SettingsApi.getFilterConfig($api, 'view_connection'),
            // SettingsApi.getFilterConfig($api, 'location'),
            // SettingsApi.getFilterConfig($api, 'assignment_job'),
            // SettingsApi.getFilterConfig($api, 'job'),
            // SettingsApi.getDemoAccounts($api, true),
        ])
            .then(response => {
                $NotificationSystem.reload();
                $customerSettings = response[2].settings;
                $accountName = response[2].webuser_name;
                try {
                    $clientSettings = ClientSettingsSchema.parse(response[3]);
                    // console.info('Valid clientSettings:', $clientSettings);
                } catch (e) {
                    console.error(
                        'Error parsing loaded settings, using default settings.',
                        response[3]
                    );
                    openModal(ModalInfo, {
                        text: $_('Error parsing loaded settings, using default settings.'),
                    });
                    $clientSettings = ClientSettingsSchema.parse(defaultClientSettings);
                }
                // $assignment_job_filter_config = response[5] || null;
                // $tactical_plan_rules_filter_config = response[6] || null;
                //
                $app.isAdmin = response[4] || false;
                $app.settingsReadonly = response[5] || null;
                // brak fallbacku: gdy payload uprawnień jest niepoprawny, toPermissions rzuca,
                // a aplikacja kończy start błędem obsłużonym w .catch() poniżej. Domyślny pełny
                // dostęp byłby tu gorszy niż komunikat o nieudanym starcie.
                $app.permissions = toPermissions(response[6]);
                Umami.init();
                // $connections_filter_config = response[5] || null;
                // $jobs_filter_config = response[6] || null;
                // $app.account = new Account().set(response[8][0], null) || null;

                $locale = $clientSettings.locale || $locale;

                appIsReady = true;
                if (initialSettings?.token === '') appIsReady = false;
                // if ($customerSettings?.map_key === '') appIsReady = false;

                // wait for draggableBorder is not undefined
                Promise.all([
                    new Promise((resolve, reject) => {
                        const interval = setInterval(() => {
                            if (draggableBorder) {
                                clearInterval(interval);
                                resolve('');
                            }
                        }, 250);
                    }),
                ]).then(_ => {
                    if (draggableBorder) {
                        let leftPage = document.querySelector('.visible') as HTMLElement;
                        let minimumPageWidth = 400;
                        let maximumPageWidth = 900;
                        leftPageWidth = Math.max(
                            parseInt(localStorage.getItem('leftPageWidth') || '600'),
                            240
                        );
                        draggableBorder.style.left =
                            leftPageWidth - draggableBorderWidth - 2 + 'px';
                        draggableBorder.style.width = draggableBorderWidth + 'px';
                        leftPage.style.width = leftPageWidth - 2 + 'px';
                        // @ts-ignore jQuery
                        (<any>jQuery(draggableBorder)).draggable({
                            axis: 'x',
                            start: (e: Event) => {},
                            drag: (e: Event, ui: any) => {
                                let moveX = ui.position.left + draggableBorderWidth; // + 2;
                                if (leftPage.parentElement) {
                                    maximumPageWidth = leftPage.parentElement.clientWidth - 100;
                                }
                                if (moveX > maximumPageWidth) {
                                    moveX = maximumPageWidth;
                                    ui.position.left = moveX - draggableBorderWidth; // -2;
                                }
                                if (moveX < minimumPageWidth) {
                                    moveX = minimumPageWidth;
                                    ui.position.left = moveX - draggableBorderWidth; // -2;
                                }
                                leftPage.style.width = moveX + 'px';
                                setTimeout(() => {
                                    window.dispatchEvent(new Event('resize'));
                                }, 0);
                            },
                            stop: (e: Event, ui: any) => {
                                localStorage.setItem('leftPageWidth', ui.position.left.toString());
                                window.dispatchEvent(new Event('resize'));
                            },
                        });
                        // draggable(draggableBorder, draggableBorderWidth, leftPageWidth, (pageWidth) => {
                        //     setTimeout( () => {
                        //         localStorage.setItem('leftPageWidth', pageWidth.toString());
                        //     }, 0);
                        // });
                    }
                });
            })
            .catch(e => {
                console.error('onMount error', e);
                // sometimes error is object with response.data.translationId
                if (typeof e == 'object' && e.response?.data?.translationId) {
                    e = e.response.data.translationId;
                    captureMessage('App.svelte onMount error (object)', { extra: e });
                }
                error = e;
                if ($accountBlocked) {
                    // konto zablokowane zwraca 403 z translationId request_permission_denied, ale
                    // ma zobaczyć pełnoekranowy komunikat, a nie zostać wyrzucone na stronę logowania
                    console.debug('account blocked - showing blocked screen instead of login');
                    return;
                }
                if (e == 'request_authentication_failed' || e == 'request_permission_denied') {
                    console.debug('redirecting to login page...');
                    let lang = localStorage.getItem('locale');
                    window.location.href =
                        '//' +
                        window.location.hostname +
                        '/login/login.html?e=' +
                        e +
                        (lang ? '&ln=' + lang : '');
                }
                let errorText = $_(
                    'Failed to start the application. Please try again shortly or contact technical support: +48 22 584 60 00'
                );
                captureMessage('App.svelte onMount error (message)', { extra: e });
                alert(errorText);
            })
            .finally(() => {
                appLoading = false;
            });
    });

    $: {
        if (
            initialSettings?.map_key === '' ||
            initialSettings?.map_key !== $customerSettings?.map_key
        ) {
            initialSettings = {
                ...initialSettings,
                map_key: $customerSettings?.map_key || '',
            };
        }
    }

    function showTemplateWindow() {
        openModal(ModalInfo, {
            text: $_(
                `<div style="text-align:left">This account is a template for one or more accounts. When changing data, remember that:<ul><li>"Realisation date" and "dates from - to" are re-dated on reset by the difference between the given date and 2024-01-10. In other words, the date 2024-01-11 in the template account will be tomorrow's date on reset.</li><li>On reset, all your data will be replaced with this template data.</li></ul></div>`
            ),
        });
    }
</script>

{#if $customerSettings.is_template}
    <div
        style="width:100%;height:26px;background:rgb(255, 120, 0);text-align: center;font-size: 14px;line-height: 26px;"
    >
        {$_('This is a template account, ')}<a href="#" on:click={showTemplateWindow} style="color:#000"
            >{$_('read more...')}</a
        >
    </div>
{/if}
<div id="page" class="main">
    {#if $accountBlocked}
        <AccountBlockedScreen />
    {:else if !appIsReady}
        {#if appLoading}{:else if $debug}
            <div
                style="width:100vw; height:100vh; display: flex; justify-content: center; align-items: center; "
            >
                <div
                    style="display: grid; width: 450px;  grid-template-columns: 50% 50%; max-height: 400px;
                    overflow: auto; grid-template-rows: min-content;  gap: 5px 10px;  grid-template-areas: '. .'; "
                >
                    <SimpleStatus
                        text="Translations"
                        check={!$isLoading}
                        success="OK"
                        failure="Loading"
                    />
                    <SimpleStatus
                        text="API Token"
                        check={initialSettings?.token !== ''}
                        success="OK"
                        failure="Not found"
                    />
                    <SimpleStatus
                        text="API Settings"
                        check={!loadingSettings && error === ''}
                        success="OK"
                        failure={(error && error) || 'Checking...'}
                    />
                    <SimpleStatus
                        text="Map Token"
                        check={initialSettings?.map_key !== ''}
                        success="OK"
                        failure="Not found"
                    />
                </div>
            </div>
        {:else}
            <div
                style="width:100vw; height:100vh; display: flex; justify-content: center; align-items: center; "
            >
                <Spinner />
            </div>
        {/if}
    {:else}
        <Router {url}>
            <MenuContextParent />
            <Modals>
                <div slot="backdrop" class="backdrop" on:click={closeModal} />
            </Modals>

            <div id="page_header_logo">
                <img src={srcLogoAutoMapaLastMile} alt="AutoMapa LastMile" />
            </div>

            <div class="main_filter_background">
                <div class="page_filters">
                    <AppMainStatus />
                </div>
            </div>

            <div class="page_header_buttons">
                <Dropdown2 icon="b" onLogout={initialSettings?.onLogout} />
            </div>

            <div id="page_header_menu">
                <!--    <PlanningModeSwitch />-->
            </div>

            <div class="filter_details"></div>

            <div class="page_content_with_menu" class:menu-vertical={true}>
                <div class="main_menu">
                    <MainMenuVertical />
                </div>

                <div
                    class="page_content"
                    class:left-side={$mainMenu.items.find(i => i.id === $mainMenu.currentPage)
                        ?.side === 'left'}
                    class:both-sides={$mainMenu.items.find(i => i.id === $mainMenu.currentPage)
                        ?.side === 'both'}
                >
                    <div class="visible" class:fullscreen-mode={$mainMenu.fullscreenMode}>
                        {#each $mainMenu.items as item, index (item.id)}
                            <Route path={item.to} component={item.component} />
                        {/each}
                    </div>
                    <div
                        class="draggable-border"
                        class:fullscreen-mode={$mainMenu.fullscreenMode}
                        bind:this={draggableBorder}
                    ></div>
                    {#if initialSettings?.map_key}
                        <TargeoMap
                            onMapLoad={map => {
                                $isMapLoaded = true;
                            }}
                            map_key={initialSettings?.map_key}
                            uu={initialSettings?.token || ''}
                        />
                    {/if}
                </div>
            </div>
        </Router>
    {/if}
</div>

<style>
    :root {
        --top-header-height: 69px;
        --bottom-header-height: 3px;
    }

    #page.main {
        display: grid;
        grid-template-rows: var(--top-header-height) var(--bottom-header-height) 1fr;
        grid-template-columns: 250px 1fr 76px;
        grid-template-areas:
            'page_header_logo main_filter_background page_header_buttons'
            'page_header_menu filter_details filter_details'
            'page_content_with_menu page_content_with_menu page_content_with_menu';
        height: 100vh;
        background-color: white;
    }

    .main_menu.planning-mode {
        /*background-color: #84c32855;*/
        /*width: 160px;*/
        /*z-index: 300;*/
    }

    .page_content_with_menu {
        grid-area: page_content_with_menu;
        border-bottom: 1px solid black;
        overflow: hidden; /* important to keep map in window */
    }

    .page_content {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        width: 100%;
    }

    .menu-vertical {
        display: flex;
        flex-direction: row;
    }

    .page_content_component {
        display: flex;
        flex-direction: row;
        justify-content: center;
        height: 100%;
    }

    .visible {
        /*flex: 1 0 700px;*/
        flex: 1 0 auto;
        /*border-right: 2px solid var(--automapa-blue-light);*/
        /*width: 600px;*/
        /*min-width: 300px;*/
        user-select: none;
        /*min-width: 699px;*/
    }
    .visible.fullscreen-mode {
        width: 100% !important;
    }

    .not-visible {
        display: none;
    }

    .page_content.left-side {
        flex-direction: row;
    }
    .page_content.both-sides {
        flex-direction: row;
    }
    .page_content.both-sides .visible {
        flex: 0 0 100%;
    }

    #page_header_logo {
        grid-area: page_header_logo;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #1c5dc7;
        padding: 16px;
    }

    #page_header_logo img {
        width: 80%;
        height: auto;
    }

    #page_header_menu {
        flex-direction: row;
        font-size: 12px;
        height: 100%;
        padding-bottom: 2px;
        box-sizing: border-box;
        background-color: #6c6b6a;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-left: 10%;
    }

    .filter_details {
        grid-area: filter_details;
        user-select: none;
        background-color: #6c6b6a;
        color: white;
        display: flex;
        flex-wrap: nowrap;
        white-space: nowrap;
        align-items: center;
        overflow: hidden;
        /*padding: 5px;*/
        padding-left: 15px;
        column-gap: 2px;
    }

    .page_header_buttons {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-content: center;
        align-items: center;
        background-color: #1c5dc7;
        z-index: 2000;
        /*padding-right: 20px;*/
    }

    .page_content {
        position: relative;
        border-top: 0px;
        /*z-index: 31;*/
    }

    .page_filters {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding-left: 2px;
        background-color: var(--analytics-status-background);
        height: 100%;
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;
        user-select: none;
        width: 100%;
        overflow: hidden;
    }

    .main_filter_background {
        grid-area: main_filter_background;
        display: flex;
        align-items: center;
        background-color: #1c5dc7;
        padding: 5px 0 0 0;
        flex: 10 1 100%;
    }

    .draggable-border {
        background-color: transparent;
        /*width: 50px;*/
        cursor: col-resize;
        position: absolute;
        height: 100%;
        border-right: 2px solid var(--automapa-blue-light);
        z-index: 200;
    }
    .draggable-border:hover {
        background-color: var(--main-selected-background);
    }
    .draggable-border.fullscreen-mode {
        display: none;
    }

    .backdrop {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        background: rgba(0, 0, 0, 0.5);
        z-index: 1001;
    }
</style>
